// Wholesale Market List: a wholesaler's profile and the deals they post.

export interface WholesalerProfile {
  full_name: string | null;
  phone: string | null;
  email: string | null;
  mailing_street: string | null;
  mailing_city: string | null;
  mailing_state: string | null;
  mailing_zip: string | null;
  acts_for_company: boolean;
  company_name: string | null;
  company_relationship: string | null;
  default_deal_type: string | null;
  default_notes: string | null;
  complete?: boolean;
  missing_required_fields?: string[];
}

export const DEAL_TYPES: Record<string, string> = {
  double_close: 'Double Close',
  fee_for_contract: 'Fee for Contract',
  currently_own: 'Currently Own',
  other: 'Other',
};

// Same property classes as list sorting and filters (Property::PropertyClasses, without Unknown)
export const PROPERTY_CLASSES = [
  'Single Family Residential', 'Townhouse', 'Condominium', 'Duplex', 'Multifamily', 'Mobile Home',
  'Residential-Vacant Land', 'Vacant Residential', 'Land', 'Farm and Ranch', 'Commercial',
];

export const COMPANY_RELATIONSHIPS = [ 'owner', 'member', 'manager', 'agent', 'other' ];

export const MAX_PHOTOS = 20;
export const MAX_VIDEOS = 2;
export const MAX_PHOTO_MB = 5;
export const MAX_VIDEO_MB = 100;

export interface WholesaleDeal {
  id: string;
  street: string;
  city: string;
  state: string;
  zipcode: string;
  property_class: string;
  sqft: number | null;
  bedrooms: string | null;
  bathrooms: string | null;
  acres: string | null;
  year_built: number | null;
  wholesale_price: string;
  expires_on: string;
  deal_type: string;
  deal_type_other: string | null;
  deal_type_label: string;
  notes: string | null;
  status: string;
  live: boolean;
  full_address: string;
  photo_urls: string[];
  video_urls: string[];
  contact?: { name: string | null; phone: string | null; email: string | null; company: string | null; relationship: string | null };
}

export type DealFormValues = Record<string, string>;

export const emptyDealForm = (profile?: WholesalerProfile | null): DealFormValues => ({
  street: '', city: '', state: profile?.mailing_state ?? '', zipcode: '', property_class: 'Single Family Residential', sqft: '', bedrooms: '', bathrooms: '', acres: '',
  year_built: '', wholesale_price: '', expires_on: '', deal_type: profile?.default_deal_type || 'fee_for_contract',
  deal_type_other: '', notes: profile?.default_notes ?? '',
});

// Problems with the chosen files, found before anything is uploaded.
export const mediaProblems = (photos: File[], videos: File[], existingPhotos = 0, existingVideos = 0): string[] => {
  const problems: string[] = [];
  if (photos.length + existingPhotos > MAX_PHOTOS) problems.push(`Up to ${MAX_PHOTOS} photos per deal.`);
  if (videos.length + existingVideos > MAX_VIDEOS) problems.push(`Up to ${MAX_VIDEOS} videos per deal.`);
  if (photos.some((file) => file.size > MAX_PHOTO_MB * 1024 * 1024)) problems.push(`Each photo must be ${MAX_PHOTO_MB} MB or smaller.`);
  if (videos.some((file) => file.size > MAX_VIDEO_MB * 1024 * 1024)) problems.push(`Each video must be ${MAX_VIDEO_MB} MB or smaller.`);
  return problems;
};

// The multipart body the server expects. A new deal leaves blank fields out; an edit sends them so a
// cleared field is cleared on the server too.
export const buildDealFormData = (values: DealFormValues, photos: File[], videos: File[], isUpdate = false): FormData => {
  const body = new FormData();
  Object.entries(values).forEach(([ key, value ]) => {
    if (isUpdate || value !== '') body.append(`wholesale_deal[${key}]`, value);
  });
  photos.forEach((file) => body.append('photos[]', file));
  videos.forEach((file) => body.append('videos[]', file));
  return body;
};
